import { dcFetch, dcFetchJson } from "./httpClient";
export interface ApiTemplate {
  id: number;
  title: string;
  name: string;
  description: string | null;
  body: string;
  locale: string;
  localeTemplateIds: Record<string, number> | null;
  deleted: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
  type: string | null;
  sectionName: string | null;
  templateId: number;
  order: number;
  seqNo: number;
  long_description: string | null;
  tagName: string | null;
}
export interface DocumentPair {
  id: string;
  enTemplate: ApiTemplate;
  frTemplate: ApiTemplate | null;
  title: string;
  createdAt: string;
  updatedAt: string;
}
export async function fetchTemplates(): Promise<ApiTemplate[]> {
  return dcFetchJson<ApiTemplate[]>("/dc-templates?deleted=false&_locale=all&_limit=1000");
}

/** Full template record including Lexical body (list endpoints may omit or truncate body). */
export async function fetchTemplateById(id: number): Promise<ApiTemplate> {
  return dcFetchJson<ApiTemplate>(`/dc-templates/${id}`);
}
export async function fetchTemplateBodies(enTemplateId: number, frTemplateId?: number | null): Promise<{
  enBody: string;
  frBody: string | null;
}> {
  const [enTemplate, frTemplate] = await Promise.all([fetchTemplateById(enTemplateId), frTemplateId ? fetchTemplateById(frTemplateId) : Promise.resolve(null)]);
  return {
    enBody: enTemplate.body,
    frBody: frTemplate?.body ?? null
  };
}
export function groupIntoPairs(templates: ApiTemplate[]): DocumentPair[] {
  const templateMap = new Map<number, ApiTemplate>();
  templates.forEach(t => templateMap.set(t.id, t));
  const pairs: DocumentPair[] = [];
  const processedFrIds = new Set<number>();
  const enTemplates = templates.filter(t => t.locale === "en");
  for (const enTemplate of enTemplates) {
    let frTemplate: ApiTemplate | null = null;
    if (enTemplate.localeTemplateIds?.fr) {
      const frId = enTemplate.localeTemplateIds.fr;
      frTemplate = templateMap.get(frId) || null;
      if (frTemplate) processedFrIds.add(frId);
    }
    pairs.push({
      id: `pair-${enTemplate.id}`,
      enTemplate,
      frTemplate,
      title: enTemplate.title || enTemplate.name || `Template #${enTemplate.id}`,
      createdAt: enTemplate.created_at,
      updatedAt: enTemplate.updated_at
    });
  }
  const frOnlyTemplates = templates.filter(t => t.locale === "fr" && !processedFrIds.has(t.id));
  for (const frTemplate of frOnlyTemplates) {
    let enTemplate: ApiTemplate | null = null;
    if (frTemplate.localeTemplateIds?.en) {
      enTemplate = templateMap.get(frTemplate.localeTemplateIds.en) || null;
    }
    if (!enTemplate) {
      pairs.push({
        id: `pair-fr-${frTemplate.id}`,
        enTemplate: frTemplate,
        frTemplate: null,
        title: frTemplate.title || frTemplate.name || `Template #${frTemplate.id}`,
        createdAt: frTemplate.created_at,
        updatedAt: frTemplate.updated_at
      });
    }
  }
  return pairs;
}
export async function updateTemplateBody(templateId: number, body: string): Promise<void> {
  const response = await dcFetch(`/dc-templates/${templateId}`, {
    method: "PUT",
    body: JSON.stringify({
      body
    })
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(`Failed to update template ${templateId}: ${response.status} ${response.statusText}${errorText ? ` — ${errorText}` : ""}`);
  }
}